// src/App.js
import React, { useState, useEffect } from 'react';
import './App.css';

// Context
import { AuthProvider } from './AuthContext';

// React Routers
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Helmet for GA
import { Helmet } from 'react-helmet';

// Layouts
import UnauthLayout from './components/appComponents/UnauthLayout';
import AuthLayout from './components/appComponents/AuthLayout';

// Pages (Unauth)
import HomePage from './unAuthPages/HomePage';
import PricingPage from './unAuthPages/PricingPage';
import SignUpPage from './unAuthPages/SignUpPage';
import SignInPage from './unAuthPages/SignInPage';
import PrivacyPolicy from './unAuthPages/PrivacyPolicy';
import TermsOfService from './unAuthPages/TermsOfService';

import WorkspaceFeature from './unAuthPages/productPages/WorkspaceFeature';
import StudioFeature from './unAuthPages/productPages/StudioFeature';
import YouTubeFeature from './unAuthPages/productPages/YouTubeFeature';
import LocalFeature from './unAuthPages/productPages/LocalFeature';

import EnterpriseAPI from './unAuthPages/apiPages/EnterpriseAPI';
import UseCasesIndex from './unAuthPages/useCases/UseCasesIndex';
import UseCasePage from './unAuthPages/useCases/UseCasePage';
import ToolsIndex from './unAuthPages/tools/ToolsIndex';
import YouTubeToolPage from './unAuthPages/tools/YouTubeToolPage';
import ChapterCheckerPage from './unAuthPages/tools/ChapterCheckerPage';

// Newsroom Routes
import NewsRoom from './unAuthPages/news/NewsRoom';
import NewsPost from './unAuthPages/news/NewsPost';
import Changelog from './unAuthPages/changelog/Changelog';

// Pages (Auth)
import WorkspacePage from './myWorkspace/WorkspacePage';
import ExistingWorkspace from './myWorkspace/workspaceComponents/ExistingWorkspacePage';
import BumpupDashboard from './myStudio/myBumpUp/bumperComponents/bumpupDashboard';
import ViewDoneBump from './myStudio/myBumpUp/doneBumpComponents/viewDoneBump';
import UserSettings from './mySettings/UserSettings';
import HelpHome from './myHelp/HelpHome';
import HelpCollection from './myHelp/HelpCollection';
import HelpArticle from './myHelp/HelpArticle';
import AskPage from './myAsk/AskPage';
import ApiPage from './myApi/ApiPage';
import DevelopersPage from './unAuthPages/developers/DevelopersPage';
import DevelopersDocsPage from './unAuthPages/developers/DevelopersDocsPage';

// 404 Pages
import NotFoundWorkspace from './components/404components/404workspace';
import NotFoundStudioUpload from './components/404components/404bumper';



// Components
import PrivateRoute from './components/appComponents/PrivateRoute';
import ScrollToTop from './components/appComponents/ScrollToTop';

import "@stripe/stripe-js";

function App() {
  // Sidebar expanded (vs icon rail) on wide screens; remembered across visits, expanded by default
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => localStorage.getItem('isSidebarOpen') !== 'false');
  useEffect(() => {
    localStorage.setItem('isSidebarOpen', isSidebarOpen);
  }, [isSidebarOpen]);
  const toggleSidebar = () => setIsSidebarOpen(open => !open);

  // Google Analytics ID (optional)
  const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Helmet>
            {/* Global Site Tag (gtag.js) - Google Analytics */}
            {GA4_MEASUREMENT_ID && [
              <script
                key="gtag-js"
                async
                src={`https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`}
              ></script>,
              <script key="gtag-init">
                {`
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${GA4_MEASUREMENT_ID}', {
                    send_page_view: false,
                  });
                `}
              </script>
            ]}
          </Helmet>

          <Routes>
            {/* Unauthenticated Layout & Routes */}
            <Route element={<UnauthLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />

              <Route path="/workspace-feature" element={<WorkspaceFeature />} />
              <Route path="/creator-feature" element={<StudioFeature />} />
              <Route path="/youtube-feature" element={<YouTubeFeature />} />
              <Route path="/local-feature" element={<LocalFeature />} />

              <Route path="/enterprise" element={<EnterpriseAPI />} />
              <Route path="/developers" element={<DevelopersPage />} />
              <Route path="/developers/docs" element={<DevelopersDocsPage />} />
              <Route path="/use-cases" element={<UseCasesIndex />} />
              <Route path="/use-cases/:slug" element={<UseCasePage />} />
              <Route path="/tools" element={<ToolsIndex />} />
              <Route path="/tools/youtube-chapter-checker" element={<ChapterCheckerPage />} />
              <Route path="/tools/youtube-timestamp-generator" element={<YouTubeToolPage slug="youtube-timestamp-generator" />} />
              <Route path="/tools/youtube-transcript" element={<YouTubeToolPage slug="youtube-transcript" />} />
              <Route path="/tools/youtube-video-summarizer" element={<YouTubeToolPage slug="youtube-video-summarizer" />} />
              <Route path="/news" element={<NewsRoom />} />
              <Route path="/news/:slug" element={<NewsPost />} />
              <Route path="/changelog" element={<Changelog />} />
            </Route>

            {/* Authenticated Layout & Routes */}
            <Route element={<PrivateRoute><AuthLayout isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} /></PrivateRoute>}>
              <Route path="/workspace" element={<WorkspacePage />} />
              {/* Old "new workspace" links — the uploader now lives on /workspace itself */}
              <Route path="/workspace/new" element={<Navigate to="/workspace" replace />} />
              <Route path="/workspace/:content_id" element={<ExistingWorkspace />} />
              <Route path="/ask" element={<AskPage />} />
              <Route path="/api/*" element={<ApiPage />} />
              <Route path="/bumper" element={<BumpupDashboard />} />
              <Route path="/bumper/:videoId" element={<ViewDoneBump />} />
              <Route path="/settings/*" element={<UserSettings />} />
              <Route path="/help" element={<HelpHome />} />
              <Route path="/help/articles/:articleSlug" element={<HelpArticle />} />
              <Route path="/help/:collectionSlug" element={<HelpCollection />} />

              <Route path="/404-workspace" element={<NotFoundWorkspace />} />
              <Route path="/404-bumper" element={<NotFoundStudioUpload />} />



            </Route>

            {/* Catch-all Redirect */}
            <Route path="*" element={<Navigate to="/workspace" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;